import {
  InstrumentInfo,
  InstrumentSummaryDetailed,
} from "@iliotech/watchlist-api";
import styles from "./InstrumentOverviewHeader.module.scss";
import { ImageWrapper } from "../../../../";

interface IProps {
  instrumentSummaryDetailed: InstrumentSummaryDetailed;
  instrumentInfo: InstrumentInfo;
}

type InstrumentType = "Portfolio" | "Bond" | "ETF" | "Crypto-Currency";

function formatToHumanReadable(number: number | null) {
  if (number === null || isNaN(number)) return "N/A";

  if (number >= 1_000_000_000_000) {
    return (number / 1_000_000_000_000).toFixed(2) + " Trillion";
  } else if (number >= 1_000_000_000) {
    return (number / 1_000_000_000).toFixed(2) + " Billion";
  } else if (number >= 1_000_000) {
    return (number / 1_000_000).toFixed(2) + " Million";
  } else if (number >= 1_000) {
    return (number / 1_000).toFixed(2) + " Thousand";
  } else {
    return number.toFixed(2);
  }
}

interface BaseInstrumentContent {
  mainInfo: {
    priceFields: Array<{
      label: string;
      value: any;
      formatter?: (value: any) => string;
    }>;
    showRecommendation?: boolean;
  };
  categories: Array<{
    title: string;
    primaryText: string;
    secondaryText?: string;
  }>;
  metrics: Array<{
    label: string;
    value: any;
    formatter?: (value: any) => string;
  }>;
  metricsPerRow?: number;
}

// Common formatters for all value types
const formatters = {
  price: (value: number | null) => `$${value?.toLocaleString() ?? "N/A"}`,
  percentage: (value: number | null) =>
    `${value ? `${(value * 100).toFixed(2)}%` : "N/A"}`,
  percentageNoMultiplication: (value: number | null) =>
    `${value ? `${value.toFixed(2)}%` : "N/A"}`,
  percentageOneDecimal: (value: number | null) =>
    `${value ? `${(value * 100).toFixed(1)}%` : "N/A"}`,
  percentageOneDecimalNoMultiplication: (value: number | null) =>
    `${value ? `${value.toFixed(1)}%` : "N/A"}`,
  decimal: (value: number | null) => `${value?.toFixed(2) ?? "N/A"}`,
  marketCap: (value: number | null) => `$${formatToHumanReadable(value)}`,
  months: (value: number | null) => (value ? `${value} months` : "N/A"),
  years: (value: number | null) => `${value?.toFixed(2) ?? "N/A"}`,
};

// Add common price fields definitions
const commonPriceFields = {
  latestClose: {
    label: "Latest Close Price",
    getValue: (data: any) => data.instrumentData.closepx_abs,
    formatter: formatters.price,
  },
  currentPrice: {
    label: "Current Price",
    getValue: (data: any) => data.instrumentData.closepx_abs,
    formatter: formatters.price,
  },
  targetPrice: {
    label: "Target Price",
    getValue: (data: any) => data.instrumentData.targetprice_abs,
    formatter: formatters.price,
  },
  ytdReturn: {
    label: "YTD Return",
    getValue: (data: any) => data.instrumentInfo.totalReturn1yPct,
    formatter: formatters.percentageNoMultiplication,
  },
};

// Common metric definitions grouped by category
const commonMetrics = {
  // Price related metrics
  price: {
    latestClose: {
      label: "Latest Close Price",
      getValue: (data: any) => data.instrumentData.closepx_abs,
      formatter: formatters.price,
    },
    targetPrice: {
      label: "Target Price",
      getValue: (data: any) => data.instrumentData.targetprice_abs,
      formatter: formatters.price,
    },
    weekHigh52: {
      label: "52Wk High",
      getValue: (data: any) => data.instrumentData.maxpxabs_1y_abs,
      formatter: formatters.price,
    },
    weekLow52: {
      label: "52Wk Low",
      getValue: (data: any) => data.instrumentData.minpxabs_1y_abs,
      formatter: formatters.price,
    },
  },

  // Market metrics
  market: {
    marketCap: {
      label: "Market Cap",
      getValue: (data: any) => data.instrumentData.marketCapitalisation,
      formatter: formatters.marketCap,
    },
    peRatio: {
      label: "Current PE Ratio",
      getValue: (data: any) => data.instrumentData.peRatio,
      formatter: formatters.decimal,
    },
    divYield: {
      label: "Div Yield",
      getValue: (data: any) => data.instrumentData.divyield_1y_pct,
      formatter: formatters.percentage,
    },
  },

  // Technical indicators
  technical: {
    rsi: {
      label: "RSI",
      getValue: (data: any) => data.instrumentData.rsi_14d_abs,
      formatter: formatters.decimal,
    },
    beta: {
      label: "Beta (1yr)",
      getValue: (data: any) => data.instrumentData.betaroll_1y_abs,
      formatter: formatters.decimal,
    },
    volatility: {
      label: "Volatility (100d)",
      getValue: (data: any) => data.instrumentData.vol_100d_pct,
      formatter: formatters.percentage,
    },
    dailyMoveAverage: {
      label: "Average Daily Move",
      getValue: (data: any) => data.instrumentInfo.vol1dDailyEquivPct,
      formatter: formatters.percentage,
    },
    vol1yr: {
      label: "Volatility (1yr)",
      getValue: (data: any) => data.instrumentInfo.vol1yPct,
      formatter: formatters.percentage,
    },
    correlation: {
      label: "Correlation",
      getValue: (data: any) => data.instrumentData.correlationroll_1y_abs,
      formatter: formatters.decimal,
    },
    volumeChange: {
      label: "1d Volume Change",
      getValue: (data: any) => data.instrumentData?.volumechange_1d_pct,
      formatter: formatters.percentage,
    },
  },

  // Return metrics
  returns: {
    totalReturn1m: {
      label: "1m Total Rtn",
      getValue: (data: any) => data.instrumentInfo.totalReturn1mPct,
      formatter: formatters.percentageOneDecimal,
    },
    totalReturn3m: {
      label: "3m Total Rtn",
      getValue: (data: any) => data.instrumentInfo.totalReturn3mPct,
      formatter: formatters.percentageOneDecimal,
    },
    totalReturn6m: {
      label: "6m Total Rtn",
      getValue: (data: any) => data.instrumentInfo.totalReturn6mPct,
      formatter: formatters.percentageOneDecimal,
    },
    totalReturn1y: {
      label: "1yr Total Rtn",
      getValue: (data: any) => data.instrumentInfo.totalReturn1yPct,
      formatter: formatters.percentageOneDecimalNoMultiplication,
    },
    totalReturn3y: {
      label: "3yr Total Rtn",
      getValue: (data: any) => data.instrumentInfo.totalReturn3yPct,
      formatter: formatters.percentageOneDecimal,
    },
  },

  // Bond specific metrics
  bond: {
    timeToMaturity: {
      label: "Time to Maturity",
      getValue: (data: any) =>
        data.instrumentData.bondDerivedData?.timeToMaturityInMonths,
      formatter: formatters.months,
    },
    yieldToMaturity: {
      label: "YTM",
      getValue: (data: any) =>
        data.instrumentData.bondDerivedData?.yieldToMaturity,
      formatter: formatters.percentageOneDecimalNoMultiplication,
    },
    runningYield: {
      label: "Running Yield",
      getValue: (data: any) =>
        data.instrumentData.bondDerivedData?.runningYieldPct,
      formatter: formatters.percentageNoMultiplication,
    },
    duration: {
      label: "Duration",
      getValue: (data: any) =>
        data.instrumentData.bondDerivedData?.durationInYears,
      formatter: formatters.years,
    },
    yieldToCall: {
      label: "YTC",
      getValue: (data: any) => data.instrumentData.bondDerivedData?.yieldToCall,
      formatter: formatters.percentageOneDecimalNoMultiplication,
    },
  },
};

const getTypeSpecificContent = (
  instrumentSummaryDetailed: InstrumentSummaryDetailed,
  instrumentInfo: InstrumentInfo
): BaseInstrumentContent => {
  const { instrumentData, sector, benchMark } = instrumentSummaryDetailed;
  const type = instrumentInfo.type as InstrumentType;

  const contentConfig: Record<InstrumentType, BaseInstrumentContent> = {
    Portfolio: {
      mainInfo: {
        priceFields: [
          {
            ...commonPriceFields.latestClose,
            value: commonPriceFields.latestClose.getValue({ instrumentData }),
          },
          {
            ...commonPriceFields.ytdReturn,
            value: commonPriceFields.ytdReturn.getValue({ instrumentInfo }),
          },
        ],
        showRecommendation: false,
      },
      categories: [
        {
          title: "Market Benchmark",
          primaryText: benchMark.name,
          secondaryText: benchMark.symbol,
        },
        {
          title: "Sector",
          primaryText: sector.name,
          secondaryText: sector.symbol,
        },
      ],
      metrics: [
        {
          ...commonMetrics.returns.totalReturn1m,
          value: commonMetrics.returns.totalReturn1m.getValue({
            instrumentInfo,
          }),
        },
        {
          ...commonMetrics.returns.totalReturn3m,
          value: commonMetrics.returns.totalReturn3m.getValue({
            instrumentInfo,
          }),
        },
        {
          ...commonMetrics.returns.totalReturn6m,
          value: commonMetrics.returns.totalReturn6m.getValue({
            instrumentInfo,
          }),
        },
        {
          ...commonMetrics.returns.totalReturn1y,
          value: commonMetrics.returns.totalReturn1y.getValue({
            instrumentInfo,
          }),
        },
        {
          ...commonMetrics.returns.totalReturn3y,
          value: commonMetrics.returns.totalReturn3y.getValue({
            instrumentInfo,
          }),
        },
        {
          ...commonMetrics.technical.vol1yr,
          value: commonMetrics.technical.vol1yr.getValue({
            instrumentInfo,
          }),
        },
        {
          ...commonMetrics.technical.beta,
          value: commonMetrics.technical.beta.getValue({ instrumentData }),
        },
        {
          ...commonMetrics.technical.dailyMoveAverage,
          value: commonMetrics.technical.dailyMoveAverage.getValue({
            instrumentInfo,
          }),
        },
        {
          ...commonMetrics.technical.correlation,
          value: commonMetrics.technical.correlation.getValue({
            instrumentData,
          }),
        },
        {
          label: "Risk/Return",
          value: instrumentInfo.riskReturn1yAbs,
          formatter: (value) => (value ? `${value.toFixed(1)}` : "N/A"),
        },
      ],
      metricsPerRow: 5,
    },
    Bond: {
      mainInfo: {
        priceFields: [
          {
            ...commonPriceFields.latestClose,
            value: commonPriceFields.latestClose.getValue({ instrumentData }),
          },
        ],
        showRecommendation: false,
      },
      categories: [
        {
          title: "Market Benchmark",
          primaryText: benchMark.name,
          secondaryText: benchMark.symbol,
        },
        {
          title: "Sector",
          primaryText: sector.name,
          secondaryText: sector.symbol,
        },
      ],
      metrics: [
        {
          ...commonMetrics.bond.timeToMaturity,
          value: commonMetrics.bond.timeToMaturity.getValue({ instrumentData }),
        },
        {
          ...commonMetrics.bond.yieldToMaturity,
          value: commonMetrics.bond.yieldToMaturity.getValue({
            instrumentData,
          }),
        },
        {
          ...commonMetrics.bond.runningYield,
          value: commonMetrics.bond.runningYield.getValue({ instrumentData }),
        },
        {
          ...commonMetrics.price.weekHigh52,
          value: commonMetrics.price.weekHigh52.getValue({ instrumentData }),
        },
        {
          ...commonMetrics.technical.beta,
          value: commonMetrics.technical.beta.getValue({ instrumentData }),
        },
        {
          ...commonMetrics.bond.duration,
          value: commonMetrics.bond.duration.getValue({ instrumentData }),
        },
        {
          ...commonMetrics.bond.yieldToCall,
          value: commonMetrics.bond.yieldToCall.getValue({ instrumentData }),
        },
        {
          ...commonMetrics.technical.volatility,
          value: commonMetrics.technical.volatility.getValue({
            instrumentData,
          }),
        },
        {
          ...commonMetrics.price.weekLow52,
          value: commonMetrics.price.weekLow52.getValue({ instrumentData }),
        },
        {
          label: "Correlation",
          value: instrumentData.correlationroll_1y_abs,
          formatter: (value) => `${value?.toFixed(2) ?? "N/A"}`,
        },
      ],
      metricsPerRow: 5,
    },
    ETF: {
      mainInfo: {
        priceFields: [
          {
            ...commonPriceFields.latestClose,
            value: commonPriceFields.latestClose.getValue({ instrumentData }),
          },
          {
            ...commonPriceFields.targetPrice,
            value: commonPriceFields.targetPrice.getValue({ instrumentData }),
          },
        ],
        showRecommendation: true,
      },
      categories: [
        {
          title: "Market Benchmark",
          primaryText: benchMark.name,
          secondaryText: benchMark.symbol,
        },
        {
          title: "Sector",
          primaryText: sector.name,
          secondaryText: sector.symbol,
        },
        {
          title: "Industry",
          primaryText: instrumentData.industry || "N/A",
        },
      ],
      metrics: [
        {
          ...commonMetrics.market.marketCap,
          value: commonMetrics.market.marketCap.getValue({ instrumentData }),
        },
        {
          ...commonMetrics.market.peRatio,
          value: commonMetrics.market.peRatio.getValue({ instrumentData }),
        },
        {
          ...commonMetrics.technical.rsi,
          value: commonMetrics.technical.rsi.getValue({ instrumentData }),
        },
        {
          ...commonMetrics.price.weekHigh52,
          value: commonMetrics.price.weekHigh52.getValue({ instrumentData }),
        },
        {
          ...commonMetrics.technical.volumeChange,
          value: commonMetrics.technical.volumeChange.getValue({
            instrumentData,
          }),
        },
        {
          ...commonMetrics.market.divYield,
          value: commonMetrics.market.divYield.getValue({ instrumentData }),
        },
        {
          ...commonMetrics.technical.beta,
          value: commonMetrics.technical.beta.getValue({ instrumentData }),
        },
        {
          ...commonMetrics.technical.volatility,
          value: commonMetrics.technical.volatility.getValue({
            instrumentData,
          }),
        },
        {
          ...commonMetrics.price.weekLow52,
          value: commonMetrics.price.weekLow52.getValue({ instrumentData }),
        },
        {
          ...commonMetrics.technical.correlation,
          value: commonMetrics.technical.correlation.getValue({
            instrumentData,
          }),
        },
      ],
      metricsPerRow: 5,
    },
    "Crypto-Currency": {
      mainInfo: {
        priceFields: [
          {
            ...commonPriceFields.currentPrice,
            value: commonPriceFields.currentPrice.getValue({ instrumentData }),
          },
        ],
        showRecommendation: false,
      },
      categories: [
        {
          title: "Market Benchmark",
          primaryText: benchMark.name,
          secondaryText: benchMark.symbol,
        },
      ],
      metrics: [
        {
          ...commonMetrics.market.marketCap,
          value: commonMetrics.market.marketCap.getValue({ instrumentData }),
        },
        {
          ...commonMetrics.technical.rsi,
          value: commonMetrics.technical.rsi.getValue({ instrumentData }),
        },
        {
          ...commonMetrics.price.weekHigh52,
          value: commonMetrics.price.weekHigh52.getValue({ instrumentData }),
        },
        {
          ...commonMetrics.technical.volumeChange,
          value: commonMetrics.technical.volumeChange.getValue({
            instrumentData,
          }),
        },
        {
          ...commonMetrics.market.divYield,
          value: commonMetrics.market.divYield.getValue({ instrumentData }),
        },
        {
          ...commonMetrics.technical.beta,
          value: commonMetrics.technical.beta.getValue({ instrumentData }),
        },
        {
          ...commonMetrics.technical.volatility,
          value: commonMetrics.technical.volatility.getValue({
            instrumentData,
          }),
        },
        {
          ...commonMetrics.price.weekLow52,
          value: commonMetrics.price.weekLow52.getValue({ instrumentData }),
        },
        {
          ...commonMetrics.technical.correlation,
          value: commonMetrics.technical.correlation.getValue({
            instrumentData,
          }),
        },
      ],
      metricsPerRow: 5,
    },
  };

  return contentConfig[type] || contentConfig.ETF;
};

export const InstrumentOverviewHeader = ({
  instrumentSummaryDetailed,
  instrumentInfo,
}: IProps) => {
  if (!instrumentSummaryDetailed) {
    return null;
  }

  const { instrument } = instrumentSummaryDetailed;
  const content = getTypeSpecificContent(
    instrumentSummaryDetailed,
    instrumentInfo
  );

  return (
    <div className={styles.container}>
      <div className={styles.headerSection}>
        <div className={styles.mainInfo}>
          <div className={styles.titleRow}>
            <ImageWrapper
              src={`https://cdn.illio.com/${instrument.iconUrl}`}
              alt={instrument?.name}
              className={styles.logo}
            />
            <h2>{`${instrument.name} - ${instrument.symbol}`}</h2>
          </div>
          <div className={styles.priceInfo}>
            {content.mainInfo.priceFields.map((field, index) => (
              <div key={index}>
                <b>{field.label}:</b>{" "}
                {field.formatter
                  ? field
                      .formatter(field.value)
                      .replace("$", instrumentInfo.ccy.label)
                  : field.value ?? "N/A"}
              </div>
            ))}
            {content.mainInfo.showRecommendation && (
              <div
                className={`${styles.recommendation} ${
                  styles[
                    instrumentSummaryDetailed.instrumentData.analystrating_stat?.toLowerCase() ??
                      "hold"
                  ]
                }`}
              >
                {instrumentSummaryDetailed.instrumentData.analystrating_stat ??
                  "HOLD"}
              </div>
            )}
          </div>
        </div>

        <div>
          {instrument.description && (
            <p className={styles.description}>{instrument.description}</p>
          )}

          <div className={styles.categoriesSection}>
            {content.categories.map((category, index) => (
              <div key={index} className={styles.category}>
                <h3>{category.title}</h3>
                <p>{category.primaryText}</p>
                {category.secondaryText && <p>({category.secondaryText})</p>}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className={styles.metricsSection}>
        <div
          className={styles.metric}
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(${content.metricsPerRow || 2}, 1fr)`,
            gap: "1rem",
          }}
        >
          {content.metrics.map((metric, index) => (
            <span key={index}>
              <b>{metric.label}:</b>{" "}
              {metric.formatter
                ? metric
                    .formatter(metric.value)
                    .replace("$", instrumentInfo.ccy.label)
                : metric.value ?? "N/A"}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};
